import {enableMapSet} from 'immer';
import React, {useEffect, useLayoutEffect} from 'react';
import {useLocation} from 'react-router-dom';
import {ThemeTokens} from '@lightricks/react-design-system';
import LtxScalerLoader from '@contentlab/components/ltx-scaler-loader';
import {ROUTES} from '@contentlab/config/routesConstants';
import useContentLabPageViewEvent from '@contentlab/hooks/analytics/use-page-view-event';
import MainRoutes from '@contentlab/routes/main';
import AnalyticsService from '@/services/analytics/AnalyticsService';
import pageViewEvents from '@/lib/marketing-events/pageViewEvents';
import {getEnv} from '@/config/environment';
import useAnalyticsLtxTeamIdentifier from '@/hooks/use-analytics-ltx-team-identifier';
import useDeltaEventScreenChanged from '@/hooks/use-delta-event-screen-changed';
import useFullStoryUser from '@/hooks/use-fullstory-user';
import useRevalidateSession from '@/hooks/use-revalidate-session';
import useSentryUser from '@/hooks/use-sentry-user';
import useSetLaunchDarklyContext from '@/hooks/use-set-launch-darkly-context';
import useSyncDeltaBaseAttributes from '@/hooks/use-sync-delta-base-attributes';
import useToken from '@/hooks/use-token';
import useZendeskScript from '@/hooks/use-zendesk-script';
import {useModestWebContext} from '@/contexts/ModestWebContext';

function updateFaviconIcon(href: string) {
  const relValues = ['icon', 'shortcut icon'];
  relValues.forEach((rel) => {
    let linkEl = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
    if (!linkEl) {
      linkEl = document.createElement('link');
      linkEl.setAttribute('rel', rel);
      document.head.appendChild(linkEl);
    }
    if (linkEl) {
      linkEl.setAttribute('href', href);
      linkEl.setAttribute('type', 'image/x-icon');
    }
  });
}

function useContentLabHeadAndTheme() {
  useLayoutEffect(() => {
    ThemeTokens.setTheme('contentlabLight');
    const previousTitle = document.title;
    updateFaviconIcon('/favicon-contentlab.ico');
    document.title = 'Ltx Scaler';
    return () => {
      ThemeTokens.setTheme('popularpayLight');
      updateFaviconIcon('/favicon.ico?v=1');
      document.title = previousTitle || 'Popular Pays';
    };
  }, []);
}

function App() {
  useContentLabHeadAndTheme();
  const {isAuthenticated} = useToken(true, true);
  useSyncDeltaBaseAttributes(isAuthenticated);
  useAnalyticsLtxTeamIdentifier(isAuthenticated);
  useDeltaEventScreenChanged();
  useContentLabPageViewEvent();

  enableMapSet();
  useSentryUser();
  useFullStoryUser();
  const location = useLocation();

  useEffect(() => {
    const pagePath = location.pathname + location.search;
    AnalyticsService.dispatchPageView(pagePath);
    pageViewEvents();
  }, [location]);

  const isRevalidatingSession = useRevalidateSession({
    signupRoute: `${ROUTES.CONTENT_LAB}${ROUTES.CONTENT_LAB_SIGNUP}${location.search ? `${location.search}` : ''}`,
    loginRoute: `${ROUTES.CONTENT_LAB}${ROUTES.CONTENT_LAB_LOGIN}${location.search ? `${location.search}` : ''}`,
  });
  const {flags, launchDarklyInitialized} = useSetLaunchDarklyContext();

  const modestContext = useModestWebContext();
  const modestInitialized = modestContext?.modestInitialized ?? false;

  const {showZendeskScript} = flags;
  useZendeskScript(getEnv().VITE_CONTENT_STUDIO_ZENDESK_KEY, showZendeskScript);

  if (isRevalidatingSession || !launchDarklyInitialized || !modestInitialized) {
    return <LtxScalerLoader />;
  }

  return <MainRoutes flags={flags} />;
}

export default App;
